import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import { 
  FiUser, 
  FiHome, 
  FiCalendar, 
  FiMessageSquare, 
  FiSave, 
  FiEdit, 
  FiPlus, 
  FiEye, 
  FiEyeOff,
  FiCheckCircle,
  FiAlertCircle,
  FiLoader,
  FiRefreshCw
} from 'react-icons/fi';

function Settings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [resyncing, setResyncing] = useState(false);
  const [resyncMessage, setResyncMessage] = useState(null);
  const [loadingPrinters, setLoadingPrinters] = useState(false);
  const [availablePrinters, setAvailablePrinters] = useState([]);
  const [recommendedPrinter, setRecommendedPrinter] = useState(null);
  const [printersError, setPrintersError] = useState(null);

  // Profile state
  const [profile, setProfile] = useState({
    name: '',
    email: ''
  });

  // Restaurant settings state
  const [restaurantSettings, setRestaurantSettings] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    gst_number: '',
    fssai_number: '',
    logo_url: '',
    logo_shape: 'circle',
    timezone: 'Asia/Kolkata',
    currency: 'INR',
    tax_rate: '5',
    include_gst_in_bill: 'true',
    service_charge_rate: '10',
    payment_methods: 'cash,card,upi,wallet',
    default_payment_method: 'cash',
    bill_print_copies: '1',
    download_bill_as_pdf: 'false',
    bill_number_counter_start: '1',
    printer_paper_width_mm: '58',
    printer_preferred_name: '',
    table_locations: 'Indoor, Outdoor, VIP, Private Room, Bar Area',
    qr_menu_show_photos: 'true',
    bill_feedback_enabled: 'true',
    review_message: 'Give us a review',
    owner_alert_whatsapp_enabled: 'false',
    owner_alert_phone: '',
    owner_alert_notify_settings: 'true',
    owner_alert_notify_menu: 'true',
    owner_alert_notify_bills: 'true',
    owner_operator_whatsapp_enabled: 'false',
    owner_operator_phone: ''
  });
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Reservation settings state
  const [reservationSettings, setReservationSettings] = useState({
    booking_advance_days: '30',
    booking_advance_hours: '2',
    opening_time: '11:00:00',
    closing_time: '23:00:00',
    time_slot_interval: '30',
    average_dining_time: '60',
    max_party_size: '20',
    min_party_size: '1',
    auto_confirm: 'true',
    send_confirmation_sms: 'true',
    send_confirmation_email: 'true',
    send_reminder_sms: 'true',
    reminder_hours_before: '2',
    cancellation_hours_before: '2'
  });

  // WhatsApp credentials state
  const [whatsappCreds, setWhatsappCreds] = useState({
    whatsapp_phone_number: null,
    whatsapp_phone_number_id: null,
    whatsapp_access_token_masked: null,
    has_credentials: false
  });
  const [isEditingWhatsApp, setIsEditingWhatsApp] = useState(false);
  const [showAccessToken, setShowAccessToken] = useState(false);
  const [whatsappForm, setWhatsappForm] = useState({
    whatsapp_phone_number: '',
    whatsapp_phone_number_id: '',
    whatsapp_access_token: ''
  });

  const configuredPaymentMethodOptions = String(restaurantSettings.payment_methods || '')
    .split(',')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);

  const uniquePaymentMethodOptions = [...new Set(configuredPaymentMethodOptions)].length > 0
    ? [...new Set(configuredPaymentMethodOptions)]
    : ['cash', 'card', 'upi', 'wallet'];

  const selectedDefaultPaymentMethod = uniquePaymentMethodOptions.includes(String(restaurantSettings.default_payment_method || '').trim().toLowerCase())
    ? String(restaurantSettings.default_payment_method || '').trim().toLowerCase()
    : uniquePaymentMethodOptions[0];

  useEffect(() => {
    loadData();
    loadConnectedPrinters();
  }, []);

  const getPrinterEndpointCandidates = () => {
    const candidates = [];
    const seen = new Set();

    const addCandidate = (url) => {
      if (!url) return;
      const normalized = String(url).replace(/\/$/, '');
      if (seen.has(normalized)) return;
      seen.add(normalized);
      candidates.push(normalized);
    };

    const origin = (typeof window !== 'undefined' && window.location && window.location.origin)
      ? window.location.origin.replace(/\/$/, '')
      : '';
    const port = (typeof window !== 'undefined' && window.location)
      ? String(window.location.port || '')
      : '';

    if (port === '9101') {
      addCandidate(`${origin}/printers`);
      addCandidate(`${origin}/print/printers`);
    }

    const configuredBase = process.env.REACT_APP_PRINT_AGENT_URL || '';

    if (configuredBase) {
      addCandidate(`${configuredBase}/printers`);
      addCandidate(`${configuredBase}/print/printers`);
    }

    addCandidate('http://localhost:9101/printers');
    addCandidate('http://localhost:9101/print/printers');
    addCandidate('http://127.0.0.1:9101/printers');
    addCandidate('http://127.0.0.1:9101/print/printers');

    return candidates;
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Load profile
      const profileRes = await api.get('/api/settings/profile');
      if (profileRes.data) {
        setProfile({
          name: profileRes.data.name || '',
          email: profileRes.data.email || ''
        });
      }

      // Load restaurant settings
      const restaurantRes = await api.get('/api/settings/restaurant');
      if (restaurantRes.data) {
        setRestaurantSettings({
          name: restaurantRes.data.name || '',
          phone: restaurantRes.data.phone || '',
          email: restaurantRes.data.email || '',
          address: restaurantRes.data.address || '',
          gst_number: restaurantRes.data.gst_number || '',
          fssai_number: restaurantRes.data.fssai_number || '',
          logo_url: restaurantRes.data.logo_url || '',
          logo_shape: restaurantRes.data.logo_shape || 'circle',
          timezone: restaurantRes.data.timezone || 'Asia/Kolkata',
          currency: restaurantRes.data.currency || 'INR',
          tax_rate: restaurantRes.data.tax_rate || '5',
          include_gst_in_bill: restaurantRes.data.include_gst_in_bill || 'true',
          service_charge_rate: restaurantRes.data.service_charge_rate || '10',
          payment_methods: restaurantRes.data.payment_methods || 'cash,card,upi,wallet',
          default_payment_method: restaurantRes.data.default_payment_method || 'cash',
          bill_print_copies: restaurantRes.data.bill_print_copies || '1',
          download_bill_as_pdf: restaurantRes.data.download_bill_as_pdf || 'false',
          bill_number_counter_start: restaurantRes.data.bill_number_counter_start || '1',
          printer_paper_width_mm: restaurantRes.data.printer_paper_width_mm || '58',
          printer_preferred_name: restaurantRes.data.printer_preferred_name || '',
          table_locations: restaurantRes.data.table_locations || 'Indoor, Outdoor, VIP, Private Room, Bar Area',
          qr_menu_show_photos: restaurantRes.data.qr_menu_show_photos || 'true',
          bill_feedback_enabled: restaurantRes.data.bill_feedback_enabled || 'true',
          review_message: restaurantRes.data.review_message || 'Give us a review',
          owner_alert_whatsapp_enabled: restaurantRes.data.owner_alert_whatsapp_enabled || 'false',
          owner_alert_phone: restaurantRes.data.owner_alert_phone || '',
          owner_alert_notify_settings: restaurantRes.data.owner_alert_notify_settings || 'true',
          owner_alert_notify_menu: restaurantRes.data.owner_alert_notify_menu || 'true',
          owner_alert_notify_bills: restaurantRes.data.owner_alert_notify_bills || 'true',
          owner_operator_whatsapp_enabled: restaurantRes.data.owner_operator_whatsapp_enabled || 'false',
          owner_operator_phone: restaurantRes.data.owner_operator_phone || ''
        });
      }

      // Load reservation settings
      const reservationRes = await api.get('/api/settings/reservations');
      if (reservationRes.data) {
        setReservationSettings(prev => ({
          ...prev,
          ...reservationRes.data
        }));
      }

      // Load WhatsApp credentials
      const whatsappRes = await api.get('/api/settings/whatsapp');
      if (whatsappRes.data) {
        setWhatsappCreds(whatsappRes.data);
        if (whatsappRes.data.has_credentials) {
          setWhatsappForm({
            whatsapp_phone_number: whatsappRes.data.whatsapp_phone_number || '',
            whatsapp_phone_number_id: whatsappRes.data.whatsapp_phone_number_id || '',
            whatsapp_access_token: ''
          });
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setSaveError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);

    try {
      await api.put('/api/settings/profile', profile);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving profile:', error);
      setSaveError(error.response?.data?.error || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRestaurant = async () => {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);

    try {
      await api.put('/api/settings/restaurant', restaurantSettings);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving restaurant settings:', error);
      setSaveError(error.response?.data?.error || 'Failed to save restaurant settings');
    } finally {
      setSaving(false);
    }
  };

  const loadConnectedPrinters = async () => {
    setLoadingPrinters(true);
    setPrintersError(null);
    try {
      const preferred = encodeURIComponent(restaurantSettings.printer_preferred_name || '');

      const candidates = getPrinterEndpointCandidates();
      let response = null;
      let lastError = null;

      for (const endpoint of candidates) {
        try {
          const current = await fetch(`${endpoint}?preferred=${preferred}`);
          if (current.ok) {
            response = current;
            break;
          }

          // Keep trying other candidates for 404/connection errors
          lastError = new Error(`HTTP ${current.status}`);
        } catch (fetchError) {
          lastError = fetchError;
        }
      }

      if (!response) {
        try {
          const health = await fetch('http://localhost:9101/health');
          if (!health.ok) {
            throw new Error('PRINT_AGENT_UNAVAILABLE');
          }
        } catch (healthError) {
          if (healthError && healthError.message === 'PRINT_AGENT_UNAVAILABLE') {
            throw healthError;
          }
        }

        throw lastError || new Error('No printer endpoint available');
      }

      const data = await response.json();
      const printers = Array.isArray(data?.printers) ? data.printers : [];

      setAvailablePrinters(printers);
      setRecommendedPrinter(data?.recommended?.name || '');
    } catch (error) {
      console.error('Failed to load connected printers:', error);
      setAvailablePrinters([]);
      setRecommendedPrinter(null);
      if (error && error.message === 'PRINT_AGENT_UNAVAILABLE') {
        setPrintersError('Print-agent is not running. Start it with: cd print-agent && npm start');
      } else {
        setPrintersError('Could not fetch printers from print-agent. Make sure print-agent is running on localhost:9101.');
      }
    } finally {
      setLoadingPrinters(false);
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    try {
      setUploadingLogo(true);
      const formData = new FormData();
      formData.append('image', file);
      formData.append('type', 'logo');
      const response = await api.post('/api/upload/image?type=logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data?.url) {
        setRestaurantSettings(prev => ({ ...prev, logo_url: response.data.url }));
      }
    } catch (error) {
      console.error('Logo upload failed:', error);
      setSaveError(error.response?.data?.error || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSaveReservations = async () => {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);

    try {
      await api.put('/api/settings/reservations', reservationSettings);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving reservation settings:', error);
      setSaveError(error.response?.data?.error || 'Failed to save reservation settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWhatsApp = async () => {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);

    try {
      const sanitizedForm = {
        whatsapp_phone_number: whatsappForm.whatsapp_phone_number,
        whatsapp_phone_number_id: (whatsappForm.whatsapp_phone_number_id || '').trim(),
        whatsapp_access_token: (whatsappForm.whatsapp_access_token || '')
          .replace(/^Bearer\s+/i, '')
          .replace(/\s+/g, '')
          .trim()
      };
      const response = await api.put('/api/settings/whatsapp', sanitizedForm);
      if (response.data) {
        // Reload credentials from API to get the latest data (including masked token)
        try {
          const whatsappRes = await api.get('/api/settings/whatsapp');
          if (whatsappRes.data) {
            setWhatsappCreds(whatsappRes.data);
            // Pre-fill form with existing values for next edit (except access token)
            if (whatsappRes.data.has_credentials) {
              setWhatsappForm({
                whatsapp_phone_number: whatsappRes.data.whatsapp_phone_number || '',
                whatsapp_phone_number_id: whatsappRes.data.whatsapp_phone_number_id || '',
                whatsapp_access_token: '' // Don't pre-fill access token for security
              });
            }
          }
        } catch (reloadError) {
          console.error('Error reloading WhatsApp credentials:', reloadError);
          // Fallback to response data if reload fails
          if (response.data.credentials) {
            setWhatsappCreds(response.data.credentials);
          }
        }
        
        setIsEditingWhatsApp(false);
        setWhatsappForm({
          whatsapp_phone_number: '',
          whatsapp_phone_number_id: '',
          whatsapp_access_token: ''
        });
        setShowAccessToken(false);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Error saving WhatsApp credentials:', error);
      setSaveError(error.response?.data?.error || 'Failed to save WhatsApp credentials');
    } finally {
      setSaving(false);
    }
  };

  const handleForceFullResync = async () => {
    // Only meaningful when running inside the local hub UI
    if (typeof window === 'undefined') return;
    const origin = window.location.origin || '';
    const port = window.location.port || '';
    if (String(port) !== '9102') {
      setResyncMessage('Force mirror is only available from the ElyDine Hub (port 9102).');
      return;
    }

    try {
      setResyncing(true);
      setResyncMessage(null);

      const response = await fetch(`${String(origin).replace(/\/$/, '')}/local/admin/full-resync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || data.error || `HTTP ${response.status}`);
      }

      setResyncMessage('✅ Full mirror triggered. Local data will refresh from cloud in a few moments.');
    } catch (error) {
      console.error('Force full resync failed:', error);
      setResyncMessage(`❌ Failed to trigger mirror: ${error.message || 'Unknown error'}`);
    } finally {
      setResyncing(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Settings" subtitle="Manage your account and restaurant settings">
        <div className="flex items-center justify-center min-h-[400px]">
          <FiLoader className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Settings" subtitle="Manage your account and restaurant settings">
      <div className="space-y-6">
        {/* Header Section */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Settings</h1>
          <p className="text-sm text-gray-600">Manage your account and restaurant settings</p>
        </div>

        {/* Success/Error Messages */}
        {saveSuccess && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
            <FiCheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-sm font-medium text-green-900">Settings saved successfully!</p>
          </div>
        )}

        {saveError && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <FiAlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm font-medium text-red-900">{saveError}</p>
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Account Settings */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <FiUser className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Account Settings</h2>
                  <p className="text-sm text-gray-500">Your personal profile information</p>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                <input
                  type="text"
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  placeholder="Enter your email"
                />
              </div>

              <button
                onClick={handleSaveProfile}
                disabled={saving}
                className="w-full bg-gray-900 hover:bg-gray-800 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm transition-colors shadow-sm"
              >
                {saving ? (
                  <>
                    <FiLoader className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FiSave className="w-4 h-4" />
                    Save Profile
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Restaurant Settings */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <FiHome className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Restaurant Settings</h2>
                  <p className="text-sm text-gray-500">Your business information</p>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Restaurant Name</label>
                <input
                  type="text"
                  value={restaurantSettings.name}
                  onChange={(e) => setRestaurantSettings({ ...restaurantSettings, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  placeholder="Enter restaurant name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    value={restaurantSettings.phone}
                    onChange={(e) => setRestaurantSettings({ ...restaurantSettings, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    placeholder="Business phone"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={restaurantSettings.email}
                    onChange={(e) => setRestaurantSettings({ ...restaurantSettings, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    placeholder="Business email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                <textarea
                  value={restaurantSettings.address}
                  onChange={(e) => setRestaurantSettings({ ...restaurantSettings, address: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  placeholder="Enter business address"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">GST Number</label>
                  <input
                    type="text"
                    value={restaurantSettings.gst_number}
                    onChange={(e) => setRestaurantSettings({ ...restaurantSettings, gst_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    placeholder="Enter GST number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">FSSAI Number</label>
                  <input
                    type="text"
                    value={restaurantSettings.fssai_number}
                    onChange={(e) => setRestaurantSettings({ ...restaurantSettings, fssai_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    placeholder="Enter FSSAI number"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bill Logo</label>
                  <div className="flex items-center gap-4">
                    <div className="h-20 w-20 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden">
                      {restaurantSettings.logo_url ? (
                        <img
                          src={restaurantSettings.logo_url}
                          alt="Restaurant logo"
                          className="h-full w-full object-contain"
                        />
                      ) : (
                        <span className="text-xs text-gray-400">No logo</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleLogoUpload(e.target.files?.[0])}
                        className="block w-full text-sm text-gray-700"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        PNG/JPG, recommended square image.
                      </p>
                      {uploadingLogo && (
                        <p className="text-xs text-gray-500 mt-1">Uploading...</p>
                      )}
                      <div className="mt-3">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Logo shape in QR menu
                        </label>
                        <select
                          value={restaurantSettings.logo_shape}
                          onChange={(e) =>
                            setRestaurantSettings({
                              ...restaurantSettings,
                              logo_shape: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-xs"
                        >
                          <option value="circle">Circle</option>
                          <option value="square">Square</option>
                          <option value="rounded">Rounded Square</option>
                        </select>
                        <p className="text-[11px] text-gray-500 mt-1">
                          Controls how your logo appears in the QR menu header.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Review Message</label>
                  <input
                    type="text"
                    value={restaurantSettings.review_message}
                    onChange={(e) => setRestaurantSettings({ ...restaurantSettings, review_message: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    placeholder="Give us a review"
                  />
                  <p className="text-xs text-gray-500 mt-1">Printed next to the feedback QR.</p>

                  <label className="flex items-start gap-3 mt-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={restaurantSettings.bill_feedback_enabled === 'true'}
                      onChange={(e) =>
                        setRestaurantSettings({
                          ...restaurantSettings,
                          bill_feedback_enabled: e.target.checked ? 'true' : 'false'
                        })
                      }
                      className="mt-0.5 w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-800">Show feedback QR & review text on printed bill</p>
                      <p className="text-xs text-gray-500 mt-1">
                        If disabled, bill footer prints “Thank you for visiting us” instead.
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Owner Alert WhatsApp Numbers</label>
                  <textarea
                    value={restaurantSettings.owner_alert_phone}
                    onChange={(e) => setRestaurantSettings({ ...restaurantSettings, owner_alert_phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    placeholder="e.g. 9198XXXXXXXX, 9188XXXXXXXX"
                    rows={2}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Add one or more numbers (comma or new line separated). All listed numbers will receive owner audit alerts.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Owner Operator WhatsApp Numbers</label>
                  <textarea
                    value={restaurantSettings.owner_operator_phone}
                    onChange={(e) => setRestaurantSettings({ ...restaurantSettings, owner_operator_phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    placeholder="e.g. 9198XXXXXXXX, 9188XXXXXXXX"
                    rows={2}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Add one or more numbers (comma or new line separated). Any listed number is treated as owner/operator in WhatsApp webhook flow.
                  </p>
                </div>

                <div className="flex items-end">
                  <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors w-full">
                    <input
                      type="checkbox"
                      checked={restaurantSettings.owner_alert_whatsapp_enabled === 'true'}
                      onChange={(e) =>
                        setRestaurantSettings({
                          ...restaurantSettings,
                          owner_alert_whatsapp_enabled: e.target.checked ? 'true' : 'false'
                        })
                      }
                      className="mt-0.5 w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-800">Enable owner audit WhatsApp alerts</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Sends change/edit alerts when important details are modified.
                      </p>
                    </div>
                  </label>
                </div>

                <div className="md:col-span-2">
                  <div className="p-3 border border-gray-200 rounded-lg">
                    <p className="text-sm font-medium text-gray-800">Owner alert notification sections</p>
                    <p className="text-xs text-gray-500 mt-1">Choose which sections should send owner WhatsApp audit alerts.</p>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={restaurantSettings.owner_alert_notify_settings === 'true'}
                          onChange={(e) =>
                            setRestaurantSettings({
                              ...restaurantSettings,
                              owner_alert_notify_settings: e.target.checked ? 'true' : 'false'
                            })
                          }
                          className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                        />
                        Settings
                      </label>
                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={restaurantSettings.owner_alert_notify_menu === 'true'}
                          onChange={(e) =>
                            setRestaurantSettings({
                              ...restaurantSettings,
                              owner_alert_notify_menu: e.target.checked ? 'true' : 'false'
                            })
                          }
                          className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                        />
                        Menu
                      </label>
                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={restaurantSettings.owner_alert_notify_bills === 'true'}
                          onChange={(e) =>
                            setRestaurantSettings({
                              ...restaurantSettings,
                              owner_alert_notify_bills: e.target.checked ? 'true' : 'false'
                            })
                          }
                          className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                        />
                        Bills
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex items-end">
                  <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors w-full">
                    <input
                      type="checkbox"
                      checked={restaurantSettings.owner_operator_whatsapp_enabled === 'true'}
                      onChange={(e) =>
                        setRestaurantSettings({
                          ...restaurantSettings,
                          owner_operator_whatsapp_enabled: e.target.checked ? 'true' : 'false'
                        })
                      }
                      className="mt-0.5 w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-800">Enable owner AI operator routing</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Owner number gets dedicated operator handling and skips customer menu/waitlist flow.
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
                  <select
                    value={restaurantSettings.timezone}
                    onChange={(e) => setRestaurantSettings({ ...restaurantSettings, timezone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  >
                    <option value="UTC">UTC</option>
                    <option value="Asia/Kolkata">IST (Asia/Kolkata)</option>
                    <option value="America/New_York">EST (America/New_York)</option>
                    <option value="America/Los_Angeles">PST (America/Los_Angeles)</option>
                    <option value="Europe/London">GMT (Europe/London)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                  <select
                    value={restaurantSettings.currency}
                    onChange={(e) => setRestaurantSettings({ ...restaurantSettings, currency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  >
                    <option value="USD">USD ($)</option>
                    <option value="INR">INR (₹)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="GBP">GBP (£)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tax Rate (%)</label>
                  <input
                    type="number"
                    value={restaurantSettings.tax_rate}
                    onChange={(e) => setRestaurantSettings({ ...restaurantSettings, tax_rate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    placeholder="5"
                    min="0"
                    max="100"
                    step="0.1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Total tax rate (CGST + SGST)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Service Charge Rate (%)</label>
                  <input
                    type="number"
                    value={restaurantSettings.service_charge_rate}
                    onChange={(e) => setRestaurantSettings({ ...restaurantSettings, service_charge_rate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    placeholder="10"
                    min="0"
                    max="100"
                    step="0.1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Service charge percentage</p>
                </div>
              </div>

              <div>
                <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={restaurantSettings.include_gst_in_bill === 'true'}
                    onChange={(e) =>
                      setRestaurantSettings({
                        ...restaurantSettings,
                        include_gst_in_bill: e.target.checked ? 'true' : 'false'
                      })
                    }
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-800">Include GST in bill</p>
                    <p className="text-xs text-gray-500 mt-1">
                      If disabled, GST (CGST/SGST) is not added and not printed on bills for this tenant.
                    </p>
                  </div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">POS Payment Methods</label>
                <input
                  type="text"
                  value={restaurantSettings.payment_methods}
                  onChange={(e) => setRestaurantSettings({ ...restaurantSettings, payment_methods: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  placeholder="cash,card,upi,wallet"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Comma-separated values. These options will appear in POS bill payment for this tenant only.
                </p>

                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Default POS Payment Method</label>
                  <select
                    value={selectedDefaultPaymentMethod}
                    onChange={(e) => setRestaurantSettings({
                      ...restaurantSettings,
                      default_payment_method: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  >
                    {uniquePaymentMethodOptions.map((method) => (
                      <option key={method} value={method}>
                        {method.toUpperCase()}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    POS will pre-select this method while generating bills.
                  </p>
                </div>

                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bill Print Copies (on Generate Bill)</label>
                  <select
                    value={restaurantSettings.bill_print_copies || '1'}
                    onChange={(e) => setRestaurantSettings({
                      ...restaurantSettings,
                      bill_print_copies: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  >
                    <option value="1">1 copy</option>
                    <option value="2">2 copies</option>
                    <option value="3">3 copies</option>
                    <option value="4">4 copies</option>
                    <option value="5">5 copies</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    When generating a bill, this many copies will print automatically. Default is 1.
                  </p>
                </div>
              </div>

              <div>
                <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={restaurantSettings.download_bill_as_pdf === 'true'}
                    onChange={(e) =>
                      setRestaurantSettings({
                        ...restaurantSettings,
                        download_bill_as_pdf: e.target.checked ? 'true' : 'false'
                      })
                    }
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-800">Enable bill PDF preview in Bills</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Shows an eye action in Generated Bills to open professionally formatted bill PDF with hotel details.
                    </p>
                  </div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bill Counter Start Number</label>
                <input
                  type="number"
                  value={restaurantSettings.bill_number_counter_start}
                  onChange={(e) => setRestaurantSettings({ ...restaurantSettings, bill_number_counter_start: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  placeholder="1"
                  min="1"
                  step="1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Next bills use sequential numbers per tenant, starting from this value if no higher bill exists.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Thermal Printer Paper Size</label>
                <select
                  value={restaurantSettings.printer_paper_width_mm}
                  onChange={(e) => setRestaurantSettings({ ...restaurantSettings, printer_paper_width_mm: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                >
                  <option value="58">58 mm</option>
                  <option value="76">76 mm</option>
                  <option value="80">80 mm</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  This controls receipt width for print-agent automatic formatting.
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Preferred Printer</label>
                  <button
                    type="button"
                    onClick={loadConnectedPrinters}
                    className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
                    disabled={loadingPrinters}
                  >
                    <FiRefreshCw className={`w-3 h-3 ${loadingPrinters ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>

                <select
                  value={restaurantSettings.printer_preferred_name}
                  onChange={(e) =>
                    setRestaurantSettings({
                      ...restaurantSettings,
                      printer_preferred_name: e.target.value
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                >
                  <option value="">Auto detect best connected ESC/POS printer</option>
                  {restaurantSettings.printer_preferred_name && !availablePrinters.some((p) => p.name === restaurantSettings.printer_preferred_name) && (
                    <option value={restaurantSettings.printer_preferred_name}>
                      {restaurantSettings.printer_preferred_name} (saved)
                    </option>
                  )}
                  {availablePrinters.map((printer) => (
                    <option key={printer.name} value={printer.name}>
                      {printer.name}
                    </option>
                  ))}
                </select>

                {recommendedPrinter && (
                  <p className="text-xs text-gray-500 mt-1">
                    Recommended by print-agent: {recommendedPrinter}
                  </p>
                )}

                {printersError && (
                  <p className="text-xs text-amber-700 mt-1">{printersError}</p>
                )}

                <p className="text-xs text-gray-500 mt-1">
                  Choose a specific printer queue from this PC, or keep Auto for automatic detection.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Table Locations</label>
                <textarea
                  value={restaurantSettings.table_locations}
                  onChange={(e) => setRestaurantSettings({ ...restaurantSettings, table_locations: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  placeholder="Indoor, Outdoor, First Floor, Second Floor"
                  rows={2}
                />
                <p className="text-xs text-gray-500 mt-1">Comma-separated list used for table locations.</p>
              </div>

              <div>
                <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={restaurantSettings.qr_menu_show_photos !== 'false'}
                    onChange={(e) =>
                      setRestaurantSettings({
                        ...restaurantSettings,
                        qr_menu_show_photos: e.target.checked ? 'true' : 'false'
                      })
                    }
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-800">Show QR mobile menu with photos</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Turn off to show the same QR menu layout without dish/category photos for this tenant only.
                    </p>
                  </div>
                </label>
              </div>

              <button
                onClick={handleSaveRestaurant}
                disabled={saving}
                className="w-full bg-gray-900 hover:bg-gray-800 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm transition-colors shadow-sm"
              >
                {saving ? (
                  <>
                    <FiLoader className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FiSave className="w-4 h-4" />
                    Save Restaurant Settings
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Reservation Settings */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm lg:col-span-2">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <FiCalendar className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Reservation Settings</h2>
                  <p className="text-sm text-gray-500">Configure your reservation system</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Booking Advance (Days)</label>
                  <input
                    type="number"
                    value={reservationSettings.booking_advance_days}
                    onChange={(e) => setReservationSettings({ ...reservationSettings, booking_advance_days: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    min="1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Booking Advance (Hours)</label>
                  <input
                    type="number"
                    value={reservationSettings.booking_advance_hours}
                    onChange={(e) => setReservationSettings({ ...reservationSettings, booking_advance_hours: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Opening Time</label>
                  <input
                    type="time"
                    value={reservationSettings.opening_time}
                    onChange={(e) => setReservationSettings({ ...reservationSettings, opening_time: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Closing Time</label>
                  <input
                    type="time"
                    value={reservationSettings.closing_time}
                    onChange={(e) => setReservationSettings({ ...reservationSettings, closing_time: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Time Slot Interval (minutes)</label>
                  <select
                    value={reservationSettings.time_slot_interval}
                    onChange={(e) => setReservationSettings({ ...reservationSettings, time_slot_interval: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  >
                    <option value="15">15 minutes</option>
                    <option value="30">30 minutes</option>
                    <option value="60">60 minutes</option>
                    <option value="90">90 minutes</option>
                    <option value="120">120 minutes</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Average Dining Time (minutes)</label>
                  <input
                    type="number"
                    value={reservationSettings.average_dining_time}
                    onChange={(e) => setReservationSettings({ ...reservationSettings, average_dining_time: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    min="15"
                    step="15"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Min Party Size</label>
                  <input
                    type="number"
                    value={reservationSettings.min_party_size}
                    onChange={(e) => setReservationSettings({ ...reservationSettings, min_party_size: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    min="1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Max Party Size</label>
                  <input
                    type="number"
                    value={reservationSettings.max_party_size}
                    onChange={(e) => setReservationSettings({ ...reservationSettings, max_party_size: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    min="1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Reminder Hours Before</label>
                  <input
                    type="number"
                    value={reservationSettings.reminder_hours_before}
                    onChange={(e) => setReservationSettings({ ...reservationSettings, reminder_hours_before: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Cancellation Hours Before</label>
                  <input
                    type="number"
                    value={reservationSettings.cancellation_hours_before}
                    onChange={(e) => setReservationSettings({ ...reservationSettings, cancellation_hours_before: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    min="0"
                  />
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={reservationSettings.auto_confirm === 'true'}
                    onChange={(e) => setReservationSettings({ ...reservationSettings, auto_confirm: e.target.checked ? 'true' : 'false' })}
                    className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                  />
                  <span className="text-sm font-medium text-gray-700">Auto Confirm Reservations</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={reservationSettings.send_confirmation_sms === 'true'}
                    onChange={(e) => setReservationSettings({ ...reservationSettings, send_confirmation_sms: e.target.checked ? 'true' : 'false' })}
                    className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                  />
                  <span className="text-sm font-medium text-gray-700">Send SMS Confirmation</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={reservationSettings.send_confirmation_email === 'true'}
                    onChange={(e) => setReservationSettings({ ...reservationSettings, send_confirmation_email: e.target.checked ? 'true' : 'false' })}
                    className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                  />
                  <span className="text-sm font-medium text-gray-700">Send Email Confirmation</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={reservationSettings.send_reminder_sms === 'true'}
                    onChange={(e) => setReservationSettings({ ...reservationSettings, send_reminder_sms: e.target.checked ? 'true' : 'false' })}
                    className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                  />
                  <span className="text-sm font-medium text-gray-700">Send SMS Reminder</span>
                </label>
              </div>

              <button
                onClick={handleSaveReservations}
                disabled={saving}
                className="w-full bg-gray-900 hover:bg-gray-800 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm transition-colors shadow-sm"
              >
                {saving ? (
                  <>
                    <FiLoader className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FiSave className="w-4 h-4" />
                    Save Reservation Settings
                  </>
                )}
              </button>
            </div>
          </div>

          {/* WhatsApp Configuration */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm lg:col-span-2">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <FiMessageSquare className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">WhatsApp Configuration</h2>
                    <p className="text-sm text-gray-500">Your WhatsApp Business API settings</p>
                  </div>
                </div>
                {!isEditingWhatsApp && (
                  <button
                    onClick={() => {
                      setIsEditingWhatsApp(true);
                      // Pre-fill form with existing credentials when editing
                      if (whatsappCreds.has_credentials) {
                        setWhatsappForm({
                          whatsapp_phone_number: whatsappCreds.whatsapp_phone_number || '',
                          whatsapp_phone_number_id: whatsappCreds.whatsapp_phone_number_id || '',
                          whatsapp_access_token: '' // Don't pre-fill access token for security
                        });
                      } else {
                        // Clear form when adding new credentials
                        setWhatsappForm({
                          whatsapp_phone_number: '',
                          whatsapp_phone_number_id: '',
                          whatsapp_access_token: ''
                        });
                      }
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm font-medium text-gray-700 transition-colors"
                  >
                    {whatsappCreds.has_credentials ? (
                      <>
                        <FiEdit className="w-4 h-4" />
                        Edit
                      </>
                    ) : (
                      <>
                        <FiPlus className="w-4 h-4" />
                        Add Credentials
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
            <div className="p-6 space-y-4">
              {isEditingWhatsApp ? (
                <>
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-700">
                      <strong>Important:</strong> Make sure you have the correct credentials from Meta Business Suite. Incorrect credentials will prevent WhatsApp messaging from working.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number (Optional)</label>
                      <input
                        type="tel"
                        value={whatsappForm.whatsapp_phone_number}
                        onChange={(e) => setWhatsappForm({ ...whatsappForm, whatsapp_phone_number: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        placeholder="+919876543210"
                      />
                      <p className="text-xs text-gray-500 mt-1">E.164 format (e.g., +919876543210)</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number ID *</label>
                      <input
                        type="text"
                        value={whatsappForm.whatsapp_phone_number_id}
                        onChange={(e) => setWhatsappForm({ ...whatsappForm, whatsapp_phone_number_id: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                        placeholder="123456789012345"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">From Meta Business Suite → API Setup</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Access Token *</label>
                      <div className="relative">
                        <input
                          type={showAccessToken ? 'text' : 'password'}
                          value={whatsappForm.whatsapp_access_token}
                          onChange={(e) => setWhatsappForm({ ...whatsappForm, whatsapp_access_token: e.target.value })}
                          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                          placeholder={whatsappCreds.has_credentials ? 'Enter new token or leave blank to keep existing' : 'EAAxxxx...'}
                          required={!whatsappCreds.has_credentials}
                        />
                        <button
                          type="button"
                          onClick={() => setShowAccessToken(!showAccessToken)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showAccessToken ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {whatsappCreds.has_credentials 
                          ? 'Leave blank to keep existing token, or enter new token to update'
                          : 'From Meta Business Suite → API Setup'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-2">
                    <button
                      onClick={handleSaveWhatsApp}
                      disabled={saving || !whatsappForm.whatsapp_phone_number_id || (!whatsappForm.whatsapp_access_token && !whatsappCreds.has_credentials)}
                      className="flex-1 bg-gray-900 hover:bg-gray-800 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm transition-colors shadow-sm"
                    >
                      {saving ? (
                        <>
                          <FiLoader className="w-4 h-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <FiSave className="w-4 h-4" />
                          Save Credentials
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingWhatsApp(false);
                        setWhatsappForm({
                          whatsapp_phone_number: '',
                          whatsapp_phone_number_id: '',
                          whatsapp_access_token: ''
                        });
                        setShowAccessToken(false);
                      }}
                      disabled={saving}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 disabled:opacity-50 font-medium text-sm transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {whatsappCreds.has_credentials ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-700">Phone Number</span>
                        <span className="text-sm text-gray-900 font-mono">
                          {whatsappCreds.whatsapp_phone_number || 'Not set'}
                        </span>
                      </div>

                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-700">Phone Number ID</span>
                        <span className="text-sm text-gray-900 font-mono">
                          {whatsappCreds.whatsapp_phone_number_id || 'Not configured'}
                        </span>
                      </div>

                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-700">Access Token</span>
                        <span className="text-sm text-gray-900 font-mono">
                          {whatsappCreds.whatsapp_access_token_masked || 'Not configured'}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="p-6 text-center border-2 border-dashed border-gray-200 rounded-lg">
                      <FiMessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-sm text-gray-600 mb-2">No WhatsApp credentials configured</p>
                      <p className="text-xs text-gray-500">Add credentials to enable WhatsApp messaging</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Local Hub Sync / Diagnostics (only relevant when running via local hub) */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm lg:col-span-2">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <FiRefreshCw className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Local Hub Sync</h2>
                  <p className="text-sm text-gray-500">
                    Force the ElyDine Hub on this machine to mirror the latest data from the cloud.
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-3">
              <p className="text-xs text-gray-500">
                This button is useful after big menu or table changes. It tells the local hub to clear its cached data
                and re‑download the latest orders, tables, menu, and other entities from the cloud backend.
              </p>
              <button
                type="button"
                onClick={handleForceFullResync}
                disabled={resyncing}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resyncing ? (
                  <>
                    <FiLoader className="w-4 h-4 animate-spin" />
                    Mirroring from cloud...
                  </>
                ) : (
                  <>
                    <FiRefreshCw className="w-4 h-4" />
                    Force Cloud → Local Mirror
                  </>
                )}
              </button>
              {resyncMessage && (
                <p className="text-xs text-gray-600 whitespace-pre-line">
                  {resyncMessage}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default Settings;

